import { Globe, Search } from 'lucide-react';
import type { InboxChannel } from '../../types/inbox';
import {
  accountLinkItems,
  type AccountExternalLinks,
  type AccountLinkKey,
} from '../../lib/accountLinks';
import { useAccountLinks } from '../../lib/useAccountLinks';
import { ChannelIcon } from './ChannelIcon';
import { cn } from '../../lib/utils';

// A row of outbound icons for an account's hand-entered external links —
// website, Facebook page, Instagram, Google Business profile, Google Ads, Meta
// ad account. Which links appear is the pure accountLinkItems: a missing or
// relative URL renders NOTHING, and with no usable links the component renders
// nothing at all (no empty strip).
//
// TWO MUTUALLY EXCLUSIVE WAYS IN (round 2 item 3):
//   links={…}     — the surface already carries the data (calendar, composer,
//                   inbox). Unchanged.
//   accountId="…" — everything else. Reads the page's ONE shared links query
//                   (useAccountLinks) instead of widening its own projection.
// The type makes passing both, or neither, a compile error.
//
// target="_blank" + rel="noopener noreferrer": these are all off-site, and on
// the composer a same-tab navigation would lose the composition.

const CHANNEL_FOR: Partial<Record<AccountLinkKey, InboxChannel>> = {
  facebookPageUrl: 'facebook',
  instagramUrl: 'instagram',
  googleBusinessProfileUrl: 'googlebusiness',
  fbAdAccountUrl: 'metaads',
};

// No channel glyph exists for Google Ads (ChannelIcon covers the inbox
// channels). A magnifier for search ads rather than a second Globe — two
// identical globes side by side would be indistinguishable without hovering.
const FALLBACK_ICON: Partial<Record<AccountLinkKey, typeof Globe>> = {
  googleAdsUrl: Search,
};

type Props =
  | { links: Partial<AccountExternalLinks> | null | undefined; accountId?: never; className?: string; only?: AccountLinkKey[] }
  | { accountId: string | null | undefined; links?: never; className?: string; only?: AccountLinkKey[] };

export function AccountLinkIcons(props: Props) {
  // Two separate components, so the hook is never called conditionally.
  return 'accountId' in props && props.accountId !== undefined ? (
    <AccountLinkIconsById accountId={props.accountId} className={props.className} only={props.only} />
  ) : (
    <LinkIconStrip links={'links' in props ? props.links : null} className={props.className} only={props.only} />
  );
}

function AccountLinkIconsById({
  accountId,
  className,
  only,
}: {
  accountId: string | null;
  className?: string;
  only?: AccountLinkKey[];
}) {
  const links = useAccountLinks();
  return <LinkIconStrip links={accountId ? links.data?.[accountId] : null} className={className} only={only} />;
}

function LinkIconStrip({
  links,
  className,
  only,
}: {
  links: Partial<AccountExternalLinks> | null | undefined;
  className?: string;
  only?: AccountLinkKey[];
}) {
  const items = accountLinkItems(links).filter((i) => !only || only.includes(i.key));
  if (items.length === 0) return null;
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {items.map((item) => {
        const channel = CHANNEL_FOR[item.key];
        const Fallback = FALLBACK_ICON[item.key] ?? Globe;
        return (
          <a
            key={item.key}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${item.label}`}
            aria-label={`Open ${item.label}`}
            className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-slate-100"
          >
            {channel ? (
              <ChannelIcon channel={channel} className="h-3.5 w-3.5" />
            ) : (
              <Fallback className="h-3.5 w-3.5 text-slate-500" aria-hidden />
            )}
          </a>
        );
      })}
    </span>
  );
}
